import { isEqual, uniqBy } from 'lodash';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';

import DaysPickerField from '@shared/forms/components/DaysPickerField/DaysPickerField';
import { WithFormField } from '@shared/forms/components/FormField';

type ParkingRequestParkingSticker = App.Domain.Parking.Data.ParkingRequestParkingStickerData;
type CrewMember = App.Domain.CrewMember.Data.CrewMemberData;
type RegistrationParking = App.Domain.Parking.Data.RegistrationParkingData;

type Props = {
  currentDays?: string[];
  registrationParking: RegistrationParking | null;
  currentParkingRequestParkingStickers?: ParkingRequestParkingSticker[] | null;
  crewMember: CrewMember | null;
};

export default function ParkingDaysField({
  name,
  label,
  required,
  currentDays = [],
  currentParkingRequestParkingStickers = [],
  registrationParking,
  crewMember,
  ...props
}: WithFormField<Props>) {
  const [error, setError] = useState<string | null>(null);
  const [both, setBoth] = useState<string[]>([]);

  useEffect(() => {
    updateDays();
  }, [crewMember, registrationParking, currentDays]);

  function updateDays() {
    let availableDays: string[] = [];

    if (registrationParking) {
      // Get parkings where there is room
      const parkingAvailableDays = registrationParking
        .amountPerDay!.filter((day) => day.amount > day.used)
        .map((day) => day.day);

      if (parkingAvailableDays.length === 0) {
        setError("You've used al your available spots on this registrationParking.");
      }

      // Get days registrationParking is open
      const parkingStickerDays: string[] = [];
      for (const sticker of registrationParking.parking!.parkingStickers!) {
        sticker.days.forEach((day) => {
          if (parkingStickerDays.indexOf(day) === -1) {
            parkingStickerDays.push(day);
          }
        });
      }

      // push in days outside event period (without limits)
      /*registrationParking.registrationParking.parkingStickers.forEach(sticker => {
        sticker.days.forEach(day => {
            if (registrationParking.enforcedDays.indexOf(day) === -1) {
                parkingAvailableDays.push(day);
            }
        });
      });*/

      availableDays = intersection(parkingStickerDays, parkingAvailableDays);
    }

    if (crewMember) {
      // days that have room when the user works
      availableDays = intersection(crewMember.days, availableDays);
    }

    // validation messages for the user
    if (crewMember && registrationParking) {
      const parkingAvailableDays = registrationParking
        .amountPerDay!.filter((day) => day.amount > day.used)
        .map((day) => day.day);

      if (availableDays.length === 0 && parkingAvailableDays.length > 0) {
        const userOnParkingDays = intersection(crewMember.days, registrationParking.enforcedDays);
        if (userOnParkingDays.length === 0) {
          setError("This crew member doesn't work when this registrationParking is opened");
        } else {
          setError(
            `You only have parking spots left for ${parkingAvailableDays.join(', ')} but this crew member does not seem to work on those days`
          );
        }
      }
    }

    availableDays = uniq([...availableDays, ...currentDays]);

    if (!isEqual(availableDays, both)) {
      setBoth(availableDays);
    }
  }

  const groupedStickers = currentParkingRequestParkingStickers!.reduce<{
    [key: string]: ParkingRequestParkingSticker & { count: number };
  }>((groupedStickers, sticker) => {
    const key = `${sticker.parkingStickerId}-${sticker.claimedAt ? 'claimed' : 'unclaimed'}`;
    if (!groupedStickers[key]) {
      groupedStickers[key] = {
        ...sticker,
        count: 1,
      };
    } else {
      groupedStickers[key].count += 1;
    }
    return groupedStickers;
  }, {});

  const events =
    Object.values(groupedStickers).map((sticker) => {
      let title = sticker.parkingSticker!.days.length > 1 ? 'Combi' : 'Day';
      const count = sticker.count > 1 ? `${sticker.count} × ` : '';

      return {
        title: count + title + (sticker.claimedAt ? ' (claimed)' : ''),
        className: sticker.claimedAt ? 'bg-red-200' : '',
        start: moment(sticker.parkingSticker!.days[0]).toDate(),
        end: moment(sticker.parkingSticker!.days[sticker.parkingSticker!.days.length - 1])
          .endOf('day')
          .toDate(),
      };
    }) || [];

  return (
    <DaysPickerField
      days={both}
      events={events}
      name={name}
      label={label}
      required={required}
      error={error ?? undefined}
      {...props}
    />
  );
}
